import { AnimatePresence, motion } from "framer-motion"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Trash2, Trophy, Clock, Dumbbell } from 'lucide-react';
import { useExercises } from '@/context/ExerciseContext';

// EmptyState component inline since it's simple
function EmptyState({ Icon, title, description }: {
  Icon: React.ComponentType<any>
  title: string
  description: string
}) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-muted/30 rounded-lg flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-muted-foreground/60" />
      </div>
      <h3 className="text-lg font-semibold mb-1">{title}</h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  )
}

export default function TodaysWorkout() {
  const { getTodayExercises, deleteExercise, loading } = useExercises();
  
  // This will re-render when exercises array changes
  const todayLogs = getTodayExercises();

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this exercise log?')) {
      await deleteExercise(id);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { x: -20, opacity: 0 },
  }

  if (todayLogs.length === 0) {
    return (
      <Card className="border-border bg-card overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold">Today's Workout</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            Icon={Dumbbell}
            title="No exercises logged"
            description="Add your first exercise to get started."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
      <Card className="border-border bg-card overflow-hidden">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold">Today's Workout</CardTitle>
      </CardHeader>
      <CardContent>
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-3">
          <AnimatePresence>
            {todayLogs.map((log) => (
              <motion.div
                key={log.id}
                variants={itemVariants}
                exit="exit"
                layout
                className="group p-3 border border-border rounded-lg hover:bg-accent/30 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium truncate">{log.exercise}</h4>
                      {log.isPersonalRecord && (
                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2 }}>
                          <Badge
                            variant="secondary"
                            className="text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400 border-yellow-500/20"
                          >
                            <Trophy className="w-3 h-3 mr-1" />
                            PR
                          </Badge>
                        </motion.div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="font-mono">
                        {log.weight}kg × {log.reps} × {log.sets}
                      </span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{formatTime(new Date(log.timestamp))}</span>
                      </div>
                    </div>
                    {log.notes && (
                      <p className="text-sm text-muted-foreground mt-1 italic">"{log.notes}"</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(log.id)}
                    disabled={loading}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-1 h-auto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </CardContent>
    </Card>
    </motion.div>
  );
}